import icArrowLeft from "@/assets/icons/ic-arrow-narrow-left.svg";
import icBriefcase from "@/assets/icons/ic-briefcase.svg";
import icCalendarDate from "@/assets/icons/ic-calendar-date.svg";
import icCheckBlue from "@/assets/icons/ic-check-blue.svg";
import icClock from "@/assets/icons/ic-clock.svg";
import icCloseRed from "@/assets/icons/ic-close-red.svg";
import icEdit from "@/assets/icons/ic-edit.svg";
import icProvider from "@/assets/icons/ic-provider.svg";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import {
  AdminAgentDetail,
  approveAgent,
  getAgentDetail,
  rejectAgent,
} from "@/services/manage-agent";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { usePermissions } from "@/lib/permissions";
import {
  formatDate,
  getCategoryType,
  MAIN_CATEGORY_LABELS,
  STATUS_CONFIG_ADMIN,
} from "@/lib/utils";

const CATEGORY_STYLES: Record<string, { bg: string; text: string }> = {
  horizontal: { bg: "bg-[rgba(255,128,19,0.11)]", text: "text-[#FF3B00]" },
  vertical: { bg: "bg-[rgba(0,122,255,0.09)]", text: "text-[#003368]" },
  enabler: { bg: "bg-[rgba(255,0,128,0.09)]", text: "text-[#66104A]" },
};

const ManageAgentDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { can } = usePermissions();
  const canUpdate = can.update("agent_management");
  const canApprove = can.create("agent_management"); // 승인/거절 = status mutation, treat as admin-only
  const [agent, setAgent] = useState<AdminAgentDetail | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchAgent = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await getAgentDetail(id);
      if (response.success) {
        setAgent(response.data);
      }
    } catch {
      toast({ description: "에이전트 정보를 불러오는데 실패했습니다." });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAgent();
  }, [fetchAgent]);

  const handleApprove = async () => {
    if (!id) return;
    try {
      await approveAgent(id);
      toast({ description: "에이전트가 승인되었습니다." });
      fetchAgent();
    } catch {
      toast({ description: "승인 처리에 실패했습니다." });
    }
  };

  const handleReject = async () => {
    if (!id) return;
    try {
      await rejectAgent(id);
      toast({ description: "에이전트가 거절되었습니다." });
      fetchAgent();
    } catch {
      toast({ description: "거절 처리에 실패했습니다." });
    }
  };

  if (!agent) {
    return null;
  }

  const statusConfig = STATUS_CONFIG_ADMIN[agent.status];

  return (
    <>
      {loading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div className="flex flex-col gap-6" style={{ display: loading ? "none" : "flex" }}>
        {/* Top Bar */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate("/agents")}
            className="w-12 h-12 flex items-center justify-center"
          >
            <img src={icArrowLeft} alt="Back" />
          </button>

          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate(`/agents/${id}/edit`)}
              className="w-[160px] h-12 pl-4 pr-5  bg-[#EBEBEB] text-[#000000] font-bold text-base gap-2"
            >
              <img src={icEdit} alt="Edit" />
              에이전트 수정
            </Button>
          </div>
        </div>

        {/* Agent Info Card */}
        <div className="bg-white rounded p-5 border border-[#00000017]">
          <div className="flex flex-col gap-3 ">
            <div className="flex flex-col gap-1">
              <h1 className="text-[32px] leading-[40px] font-normal text-[#000000]">
                {agent.name}
              </h1>
              <p className="text-sm text-[#000000] whitespace-pre-line break-words">{agent.short_description}</p>
            </div>

            <div className="py-1">
              <div className="h-px bg-[rgba(0,0,0,0.09)]" />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src={icProvider} alt="" className="w-5 h-5" />
                <div className="flex items-center gap-3">
                  <span className="text-sm text-[#B0B0B0]">공급사</span>
                  <span className="text-base text-[#000000] max-w-[200px] break-words whitespace-pre-line">
                    {agent.provider}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <img src={icBriefcase} alt="" className="w-5 h-5" />
                <div className="flex items-center gap-3">
                  <span className="text-sm text-[#B0B0B0]">적용 분야</span>
                  <span className="text-base text-[#000000] max-w-[200px] break-words whitespace-pre-line">
                    {agent.application_area}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <img src={icCalendarDate} alt="" className="w-5 h-5" />
                <div className="flex items-center gap-3">
                  <span className="text-sm text-[#B0B0B0]">등록일</span>
                  <span className="text-base text-[#000000]">{formatDate(agent.created_at)}</span>
                </div>
              </div>

              <div
                className={`inline-flex items-center justify-center gap-1 h-6 rounded-[4px] ${statusConfig.bg} ${statusConfig.text} ${statusConfig.padding}`}
              >
                <span className="text-sm leading-5 font-normal">{statusConfig.label}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content: Two columns */}
        <div className="flex gap-6 ">
          {/* Left: Solution Overview (fixed width per Figma: 685px) */}
          <div className="w-[66%] bg-white rounded p-5 flex flex-col gap-3 border border-[#00000017]">
            <h2 className="text-lg font-bold text-[#000000]">솔루션 개요</h2>
            <p className="text-sm text-[#000000] break-words whitespace-pre-line">
              {agent.solution}
            </p>
          </div>

          {/* Right: Categories + Additional Info */}
          <div className=" flex flex-col gap-6" style={{ width: "32%" }}>
            {agent.main_category && (
              <div className="flex flex-col gap-3 p-5 rounded border border-black/15">
                <h3 className="text-lg font-bold leading-6 tracking-[-0.01em] text-[#000000]">
                  대분류
                </h3>
                <span className="text-sm leading-5 text-[#202020] whitespace-pre-line break-all">
                  {agent.main_category}
                </span>
              </div>
            )}

            {/* Categories */}
            <div className="bg-white rounded p-5 flex flex-col gap-3 border border-black/15">
              <h2 className="text-lg font-bold text-[#000000]">
                기능 카테고리{" "}
                {/* <span className="text-base font-normal text-[#646464]">
                ({agent.categories.length} selected)
              </span> */}
              </h2>
              <div className="flex flex-wrap gap-2">
                <span className="text-sm font-medium whitespace-pre-line break-all">
                  {agent.categories}
                </span>
              </div>
            </div>

            {/* Additional Info */}
            <div className="bg-white rounded p-5 flex flex-col gap-6 border border-[#00000017]">
              <div className="flex flex-col gap-2">
                <h2 className="text-lg font-bold text-[#000000]">추가 정보</h2>
                <div className="flex flex-col">
                  <div className="flex justify-between gap-5 items-baseline py-3 border-b border-[rgba(0,0,0,0.09)]">
                    <span className="text-sm text-[#000000] shrink-0">등록자</span>
                    <span className="text-sm text-[#000000] whitespace-pre-line break-all">
                      {agent.created_by_name}
                    </span>
                  </div>
                  <div className="flex justify-between items-baseline pt-3">
                    <span className="text-sm text-[#000000]">최종 수정일</span>
                    <span className="text-sm text-[#000000]">{formatDate(agent.updated_at)}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => navigate(`/agents/${id}/history`)}
                className="flex w-full items-center justify-center gap-1 h-8 px-3 pl-2 rounded bg-[#EBEBEB] text-xs font-bold text-[#000000]"
              >
                <img src={icClock} alt="" className="w-4 h-4" />
                수정 이력 보기
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageAgentDetailPage;
